import { createContext, useContext, useState, type ReactNode } from "react";

export type Preset = "today" | "yesterday" | "this_week" | "last_7" | "this_month" | "last_month" | "last_30" | "this_year";

/** Whole local days: `start` is the first day's midnight, `end` the midnight after the last day. `preset` names a quick pick, null for a range picked on the calendar or stepped to. */
export interface Period {
  start: Date;
  end: Date;
  preset: Preset | null;
}

export const PRESETS: Array<{ key: Preset; label: string }> = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "this_week", label: "This week" },
  { key: "last_7", label: "Last 7 days" },
  { key: "this_month", label: "This month" },
  { key: "last_month", label: "Last month" },
  { key: "last_30", label: "Last 30 days" },
  { key: "this_year", label: "This year" },
];

interface RangeScopeState {
  period: Period;
  setPeriod: (p: Period) => void;
  /** "Compare with the days before": off hides the vs-previous changes. */
  compare: boolean;
  setCompare: (on: boolean) => void;
}

const RangeScopeContext = createContext<RangeScopeState | null>(null);

/**
 * The period Reports and Bills show (canvas v2-o-analytics-range-proposed): picked in the shell's head, read by whichever
 * page is mounted under it, hence a context instead of prop-drilling through Outlet. Overview always shows today.
 */
export function RangeScopeProvider({ children }: { children: ReactNode }) {
  const [period, setPeriod] = useState<Period>(() => presetPeriod("today", new Date()));
  const [compare, setCompare] = useState(true);
  return <RangeScopeContext.Provider value={{ period, setPeriod, compare, setCompare }}>{children}</RangeScopeContext.Provider>;
}

export function useRangeScope(): RangeScopeState {
  const ctx = useContext(RangeScopeContext);
  if (!ctx) throw new Error("useRangeScope must be used within RangeScopeProvider");
  return ctx;
}

export const dayStart = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
export const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
export const periodDays = (p: { start: Date; end: Date }) => Math.max(1, Math.round((p.end.getTime() - p.start.getTime()) / 86_400_000));
const isMonthStart = (d: Date) => d.getDate() === 1;
/** Whole calendar months (1st to 1st): stepping and comparing go by month, not by day count. */
function wholeMonths(p: Period): number {
  if (!isMonthStart(p.start) || !isMonthStart(p.end)) return 0;
  return (p.end.getFullYear() - p.start.getFullYear()) * 12 + p.end.getMonth() - p.start.getMonth();
}

export function presetPeriod(preset: Preset, now: Date): Period {
  const today = dayStart(now);
  const tomorrow = addDays(today, 1);
  const span = (start: Date, end: Date): Period => ({ start, end, preset });
  switch (preset) {
    case "today":
      return span(today, tomorrow);
    case "yesterday":
      return span(addDays(today, -1), today);
    case "this_week":
      return span(addDays(today, -((today.getDay() + 6) % 7)), tomorrow); // weeks start on Monday, as on the calendar
    case "last_7":
      return span(addDays(today, -6), tomorrow);
    case "this_month":
      return span(new Date(today.getFullYear(), today.getMonth(), 1), tomorrow);
    case "last_month":
      return span(new Date(today.getFullYear(), today.getMonth() - 1, 1), new Date(today.getFullYear(), today.getMonth(), 1));
    case "last_30":
      return span(addDays(today, -29), tomorrow);
    case "this_year":
      return span(new Date(today.getFullYear(), 0, 1), tomorrow);
  }
}

/** First and last picked day (both inclusive) as a period. */
export function daysPeriod(first: Date, last: Date): Period {
  const [a, b] = first <= last ? [first, last] : [last, first];
  return { start: dayStart(a), end: addDays(b, 1), preset: null };
}

/** The ‹ › keys: the same span one step back or forward. "This month" so far steps to whole months; "today" to single days. */
export function stepPeriod(p: Period, dir: 1 | -1, now: Date): Period {
  const months = wholeMonths(p) || (p.preset === "this_month" ? 1 : p.preset === "this_year" ? 12 : 0);
  if (months) {
    const start = new Date(p.start.getFullYear(), p.start.getMonth() + dir * months, 1);
    const next: Period = { start, end: new Date(start.getFullYear(), start.getMonth() + months, 1), preset: null };
    return clampToToday(next, now);
  }
  const n = periodDays(p);
  return clampToToday({ start: addDays(p.start, dir * n), end: addDays(p.end, dir * n), preset: null }, now);
}

/** A period never runs past today; landing on today's month or day gives it back its quick-pick name. */
function clampToToday(p: Period, now: Date): Period {
  const tomorrow = addDays(dayStart(now), 1);
  const end = p.end > tomorrow ? tomorrow : p.end;
  const match = PRESETS.find((x) => {
    const q = presetPeriod(x.key, now);
    return q.start.getTime() === p.start.getTime() && q.end.getTime() === end.getTime();
  });
  return { start: p.start, end, preset: match?.key ?? null };
}

/** Can the › key move on? Not once the period reaches today. */
export const canStepForward = (p: Period, now: Date) => p.end < addDays(dayStart(now), 1);

/** Inclusive start / exclusive end of the period, the shape every page filters with. */
export function rangeBounds(p: Period): { start: Date; end: Date } {
  return { start: p.start, end: p.end };
}

/** The span just before, for "vs" changes: the same number of days, or the same number of whole months. */
export function previousRangeBounds(p: Period): { start: Date; end: Date } {
  const months = wholeMonths(p) || (p.preset === "this_month" ? 1 : p.preset === "this_year" ? 12 : 0);
  if (months) return { start: new Date(p.start.getFullYear(), p.start.getMonth() - months, 1), end: p.start };
  return { start: addDays(p.start, -periodDays(p)), end: p.start };
}

const fmtDay = (d: Date, withYear = false) => d.toLocaleDateString("en-GB", { day: "numeric", month: "short", ...(withYear ? { year: "numeric" } : {}) });

/** The period key's two lines: its name ("Last 7 days", or the dates) and the dates it covers. */
export function periodLabel(p: Period, now: Date): { title: string; dates: string } {
  const last = addDays(p.end, -1);
  const otherYear = p.start.getFullYear() !== now.getFullYear();
  const dates =
    periodDays(p) === 1
      ? fmtDay(p.start, otherYear)
      : p.start.getMonth() === last.getMonth() && p.start.getFullYear() === last.getFullYear()
        ? `${p.start.getDate()} – ${fmtDay(last, otherYear)}`
        : `${fmtDay(p.start, p.start.getFullYear() !== last.getFullYear() || otherYear)} – ${fmtDay(last, otherYear)}`;
  const name = PRESETS.find((x) => x.key === p.preset)?.label;
  if (name) return { title: name, dates };
  const months = wholeMonths(p);
  if (months === 1) return { title: p.start.toLocaleDateString("en-GB", { month: "long", year: "numeric" }), dates };
  if (months === 12 && p.start.getMonth() === 0) return { title: String(p.start.getFullYear()), dates };
  return { title: periodDays(p) === 1 ? p.start.toLocaleDateString("en-GB", { weekday: "long" }) : `${periodDays(p)} days`, dates };
}

/** "vs yesterday", "vs last month", "vs the 7 days before". */
export function vsLabel(p: Period): string {
  if (p.preset === "today") return "vs yesterday";
  const months = wholeMonths(p) || (p.preset === "this_month" ? 1 : p.preset === "this_year" ? 12 : 0);
  if (months === 1) return "vs the month before";
  if (months === 12) return "vs the year before";
  const n = periodDays(p);
  return n === 1 ? "vs the day before" : `vs the ${n} days before`;
}

/** "today", "this week", … for sentences; empty for a picked range. */
export function periodWord(p: Period): string {
  const words: Record<Preset, string> = { today: "today", yesterday: "yesterday", this_week: "this week", last_7: "in the last 7 days", this_month: "this month", last_month: "last month", last_30: "in the last 30 days", this_year: "this year" };
  return p.preset ? words[p.preset] : "";
}

// ---- time buckets for the sales charts (Overview, Reports) ----

export interface Bucket {
  label: string;
  start: Date;
  end: Date;
  revenue: number;
  orderCount: number;
}

function fmtHourLabel(d: Date): string {
  const h = d.getHours();
  const period = h < 12 ? "a" : "p";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}${period}`;
}

/** One day: 2-hour blocks. Up to two months: days. Longer: months. */
export function buildBuckets(p: { start: Date; end: Date }): Bucket[] {
  const days = periodDays(p);
  if (days === 1) {
    return Array.from({ length: 12 }, (_, i) => {
      const start = new Date(p.start);
      start.setHours(i * 2);
      const end = new Date(start);
      end.setHours(start.getHours() + 2);
      return { label: fmtHourLabel(start), start, end, revenue: 0, orderCount: 0 };
    });
  }
  if (days <= 62) {
    return Array.from({ length: days }, (_, i) => {
      const start = addDays(p.start, i);
      const label = days <= 7 ? start.toLocaleDateString(undefined, { weekday: "short" }) : String(start.getDate());
      return { label, start, end: addDays(start, 1), revenue: 0, orderCount: 0 };
    });
  }
  const buckets: Bucket[] = [];
  for (let m = new Date(p.start.getFullYear(), p.start.getMonth(), 1); m < p.end; m = new Date(m.getFullYear(), m.getMonth() + 1, 1)) {
    buckets.push({ label: m.toLocaleDateString(undefined, { month: "short" }), start: m < p.start ? p.start : m, end: new Date(m.getFullYear(), m.getMonth() + 1, 1), revenue: 0, orderCount: 0 });
  }
  return buckets;
}

/** What each Sales Trend / Orders-by-X bucket actually spans — lowercase for "Revenue by {unit}", title-case for "Orders by {Unit}". */
export function bucketUnitLabel(p: { start: Date; end: Date }): { lower: string; title: string } {
  const days = periodDays(p);
  if (days === 1) return { lower: "2-hour block", title: "Hour" };
  if (days <= 62) return { lower: "day", title: "Day" };
  return { lower: "month", title: "Month" };
}

export function fillBuckets(buckets: Bucket[], orders: ReadonlyArray<{ payment_status: string; total: number; created_at: string }>): Bucket[] {
  const filled = buckets.map((b) => ({ ...b }));
  for (const o of orders) {
    if (o.payment_status !== "paid") continue;
    const t = new Date(o.created_at).getTime();
    const b = filled.find((b) => t >= b.start.getTime() && t < b.end.getTime());
    if (b) {
      b.revenue += o.total;
      b.orderCount += 1;
    }
  }
  return filled;
}
